/**
 * Modul-Singleton mit den Flags aus dem System-Item FEATURE_FLAGS.
 *
 * Geladen wird einmal pro Seitenaufruf, so frueh wie moeglich (onClientEntry
 * in gatsby-browser.js). Bis die Flags da sind, liefert alles null bzw.
 * Control. Kommen sie nicht innerhalb von LOAD_TIMEOUT_MS, bleibt die Seite
 * fuer diesen Aufruf bei Control, auch wenn die Antwort spaeter noch eintrifft.
 * Lieber Status quo als ein Umschalten mitten im Lesen. Fehler oder ein
 * fehlendes Item heissen ebenfalls „keine Flags".
 */
import { API } from 'aws-amplify'

import { getSystem } from '../graphql/queries'
import { BUILD_ENV, isBrowser } from '../utils'
import {
  applyFunnelParams,
  lpAssignments,
  mergeQaOverrides,
  parseFlagsData,
  resolveFlag,
  stageFrom
} from './helpers.js'
import { getVisitorId, watchConsent } from './visitor-id'

export const FLAGS_SYSTEM_ID = 'FEATURE_FLAGS'
export const LOAD_TIMEOUT_MS = 1500
export const STAGE = stageFrom(BUILD_ENV)

const QA_STORAGE_KEY = 'aivy_ff_overrides'

// status: idle | loading | ready | timeout | error
export const IDLE_STATE = Object.freeze({ status: 'idle', flags: {} })

let state = IDLE_STATE
let started = false
const listeners = new Set()

const setState = (next) => {
  state = next
  listeners.forEach((listener) => listener(state))
}

export const getState = () => state

export const subscribe = (listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const describeError = (err) =>
  err?.errors?.[0]?.message || err?.message || String(err)

const loadFlags = () => {
  setState({ status: 'loading', flags: {} })

  let settled = false
  const settle = (next) => {
    if (settled) return false
    settled = true
    setState(next)
    return true
  }

  const timer = window.setTimeout(() => {
    if (settle({ status: 'timeout', flags: {} })) {
      console.warn(
        `[experiments] Flags nach ${LOAD_TIMEOUT_MS} ms nicht geladen, dieser Seitenaufruf bleibt bei Control`
      )
    }
  }, LOAD_TIMEOUT_MS)

  Promise.resolve()
    .then(() =>
      API.graphql({
        query: getSystem,
        variables: { id: FLAGS_SYSTEM_ID },
        authMode: 'AWS_IAM'
      })
    )
    .then((res) => {
      settle({
        status: 'ready',
        flags: parseFlagsData(res?.data?.getSystem?.data)
      })
    })
    .catch((err) => {
      if (settle({ status: 'error', flags: {} })) {
        console.warn(
          '[experiments] Flags nicht geladen, Seite bleibt bei Control:',
          describeError(err)
        )
      }
    })
    .finally(() => window.clearTimeout(timer))
}

let qaOverrides = null

/**
 * ?ff_<key>=<variant> fuer QA. Gilt fuer die Tab-Session (sessionStorage) und
 * zaehlt nie als Exposure.
 */
export const getQaOverrides = () => {
  if (qaOverrides) return qaOverrides
  if (!isBrowser) return {}

  let stored = {}
  try {
    stored = JSON.parse(window.sessionStorage.getItem(QA_STORAGE_KEY)) || {}
  } catch {
    stored = {}
  }

  const { overrides, changed } = mergeQaOverrides(
    window.location.search,
    stored
  )
  if (changed) {
    try {
      window.sessionStorage.setItem(QA_STORAGE_KEY, JSON.stringify(overrides))
    } catch {
      // ohne sessionStorage gilt der Override nur fuer diesen Aufruf
    }
  }

  qaOverrides = overrides
  return qaOverrides
}

export const buildContext = () => ({
  visitorId: getVisitorId(),
  stage: STAGE
})

/** Startet Consent-Abgleich, Besucher-ID und Flag-Laden. Idempotent. */
export const startExperiments = () => {
  if (!isBrowser || started) return
  started = true
  watchConsent()
  getVisitorId()
  getQaOverrides()
  loadFlags()
}

/** Fuer Code ausserhalb von React. In Komponenten useVariant nehmen. */
export const getVariantSync = (key) => {
  const { flags } = state
  if (!flags[key]) return null
  return resolveFlag(flags, key, buildContext(), getQaOverrides()).variant
}

/**
 * Ergaenzt die Query-Parameter der Funnel-iframe-URL um vid, exp_<key> (jedes
 * laufende Flag mit surface 'lp') und die QA-Overrides.
 */
export const appendExperimentParams = (params) => {
  if (!isBrowser) return params
  const ctx = buildContext()
  const qa = getQaOverrides()
  return applyFunnelParams(params, {
    visitorId: ctx.visitorId,
    assignments: lpAssignments(state.flags, ctx, qa),
    qa
  })
}
